import React, { useState, useMemo, useEffect } from 'react';
import type { Options, Placement } from '@popperjs/core';
import { usePopper } from 'react-popper';
import Portal from '@reach/portal';
import { Box } from './core';

export interface IPopoverProps {
  content: React.ReactNode;
  show: boolean;
  children: React.ReactNode;
  placement?: Placement;
}

export const Popover = ({
  content,
  show,
  children,
  placement = 'auto',
}: IPopoverProps): JSX.Element => {
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const options = useMemo(
    (): Options => ({
      placement,
      strategy: 'fixed',
      modifiers: [
        { name: 'offset', options: { offset: [8, 8] } },
        { name: 'arrow', options: { element: arrowElement } },
        { name: 'preventOverflow', options: { padding: 8 } },
      ],
    }),
    [arrowElement, placement]
  );

  const { styles, update, attributes } = usePopper(
    referenceElement,
    popperElement,
    options
  );

  useEffect(() => {
    // update popover position when toggled in case there were layout shifts
    update && update();
  }, [show]);

  return (
    <>
      <Box /* Reference Element */ ref={setReferenceElement as any}>
        {children}
      </Box>
      <Portal>
        <Box /* Popover Container */
          ref={setPopperElement as any}
          style={styles.popper}
          {...attributes.popper}
          sx={{
            zIndex: 9999,
            visibility: show ? 'visible' : 'hidden',
            opacity: show ? 1 : 0,
            transition: 'visibility 150ms linear, opacity 150ms linear',
            color: 'secondary',
          }}
        >
          {content}
          <Box /* Arrow */
            sx={{
              width: '8px',
              height: '8px',
              zIndex: 9998,
              '::before': {
                position: 'absolute',
                width: '8px',
                height: '8px',
                zIndex: 9998,
                content: "''",
                border: '1px solid black',
                borderColor: 'muted',
                transform: 'rotate(45deg)',
                backgroundColor: 'background',
              },
              '&.arrow-top': {
                bottom: '-4px',
                '::before': {
                  borderTop: 'none',
                  borderLeft: 'none',
                },
              },
              '&.arrow-bottom': {
                top: '-4px',
                '::before': {
                  borderBottom: 'none',
                  borderRight: 'none',
                },
              },
              '&.arrow-left': {
                right: '-4px',
                '::before': {
                  borderBottom: 'none',
                  borderLeft: 'none',
                },
              },
              '&.arrow-right': {
                left: '-4px',
                '::before': {
                  borderRight: 'none',
                  borderTop: 'none',
                },
              },
            }}
            className={`arrow-${
              attributes.popper?.['data-popper-placement'] ?? ''
            }`}
            ref={setArrowElement as any}
            style={styles.arrow}
            {...attributes.arrow}
          />
        </Box>
      </Portal>
    </>
  );
};
